/**
 * CalendarShareView — the designers' collaborative workspace for a content
 * calendar, served at /c/<calendarId> (public link, no login). Multiple
 * designers use the SAME link at once (like a shared sheet): each taps their
 * name once, then claims posts, drops the asset link, and marks them done.
 *
 * Every action goes through the atomic `mark_calendar_post` RPC so simultaneous
 * edits by different designers never clobber each other, and it all syncs live
 * — to everyone on the link AND to the managers' in-app calendar for approval.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { supabase, ORG_ID } from '../utils/supabaseClient';
import { monthLabel, empName } from './content/contentShared';
import { logoForClientName, logoUrl } from '../utils/brandLogos';
import { usePresence, PresenceBar } from '../components/Presence';
import { celebrate } from '../utils/celebrate';
import LoginScreen from '../components/LoginScreen';
import { restoreSession } from '../utils/auth';

// STR palette — obsidian ground, bone type, brass hairline accent.
const B = { GOLD: '#A98953', INK: '#0A0A0C', SURF: '#141417', SURF2: '#17171A', LINE: 'rgba(239,235,228,0.12)', TXT: '#EFEBE4', CAP: 'rgba(239,235,228,0.80)', MUT: 'rgba(239,235,228,0.62)', FAINT: 'rgba(239,235,228,0.42)' };
const STATUS = {
  todo:      { label: 'Open',            bg: 'rgba(239,235,228,0.08)', fgc: 'rgba(239,235,228,0.72)' },
  submitted: { label: 'Done · in review', fgc: '#A98953', bg: 'rgba(169,137,83,0.16)' },
  changes:   { label: 'Changes needed',  fgc: '#C79A5B', bg: 'rgba(199,154,91,0.16)' },
  approved:  { label: 'Approved',        fgc: '#8FB08A', bg: 'rgba(143,176,138,0.16)' },
};
const inp = { width: '100%', padding: '8px 10px', fontSize: 13, background: B.SURF2, border: `1px solid ${B.LINE}`, borderRadius: 'var(--radius)', color: B.TXT, boxSizing: 'border-box', fontFamily: 'var(--font)' };
const pill = (s) => ({ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 'var(--radius)', background: s.bg, color: s.fgc, fontFamily: 'var(--mono)' });

// Render text with any http(s) URLs as clickable links (so designers can open
// raw-footage / inspiration links straight from the share page).
function Linky({ text }) {
  return String(text || '').split(/(https?:\/\/[^\s]+)/g).map((p, i) =>
    /^https?:\/\//.test(p)
      ? <a key={i} href={p} target="_blank" rel="noreferrer" style={{ color: B.GOLD, wordBreak: 'break-all' }}>{p}</a>
      : <span key={i}>{p}</span>
  );
}

function Wrap({ children }) {
  return <div style={{ minHeight: '100vh', background: B.INK, color: B.TXT, fontFamily: 'var(--font)' }}>
    <div style={{ maxWidth: 1040, margin: '0 auto', padding: '40px 20px 72px' }}>{children}</div>
  </div>;
}

// The calendar workspace is for staff (designers), so it needs a staff sign-in like the
// app itself — the database no longer answers anyone without one. Signing in here also
// signs you into the app (same browser session), and identifies you as the designer.
export default function CalendarShareView({ calId }) {
  const [session, setSession] = useState(undefined);       // undefined = still checking
  useEffect(() => { restoreSession().then(setSession); }, []);
  if (session === undefined) return null;
  if (!session) return <LoginScreen onLogin={() => restoreSession().then(setSession)} />;
  return <CalendarShareInner calId={calId} />;
}

function CalendarShareInner({ calId }) {
  const [cal, setCal] = useState(null);
  const [emps, setEmps] = useState([]);
  const [phase, setPhase] = useState('loading'); // loading | ok | missing
  // Identity: an explicit pick on this device wins; otherwise fall back to the
  // employee id saved at portal login (so a logged-in designer skips the picker).
  const [me, setMe] = useState(() => { try { return localStorage.getItem('cal_me_id') || localStorage.getItem('str_emp_id') || ''; } catch { return ''; } });
  const [busy, setBusy] = useState({});
  const [linkEdits, setLinkEdits] = useState({});
  const [toast, setToast] = useState('');

  // Live presence — designers on the same link see each other in real time.
  const presenceMe = { id: me || '', name: empName(emps, me) || 'Designer' };
  const { peers, setEditing } = usePresence(me ? 'cal:' + calId : null, presenceMe);
  useEffect(() => { setEditing(Object.keys(linkEdits).length > 0); }, [linkEdits, setEditing]);

  const load = useCallback(async () => {
    const { data, error } = await supabase.from('content_calendars').select('*').eq('org_id', ORG_ID).eq('id', calId).maybeSingle();
    if (error || !data) { setPhase('missing'); return; }
    setCal(data); setPhase('ok');
  }, [calId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { supabase.from('employees').select('id,name').eq('org_id', ORG_ID).then(({ data }) => setEmps(data || [])); }, []);
  useEffect(() => {
    const ch = supabase.channel('calshare_' + calId)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'content_calendars', filter: 'id=eq.' + calId }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [calId, load]);

  function flash(m) { setToast(m); setTimeout(() => setToast(''), 2600); }
  function pickMe(id) { setMe(id); try { localStorage.setItem('cal_me_id', id); } catch (e) { /* */ } }

  async function act(postId, action, value) {
    setBusy(b => ({ ...b, [postId]: true }));
    const { error } = await supabase.rpc('mark_calendar_post', { p_org: ORG_ID, p_cal: calId, p_post: postId, p_action: action, p_emp: me, p_value: value ?? null });
    if (error) flash('Could not save — check your connection and try again.');
    else {
      if (action === 'done') celebrate('submit');          // design handed in
      else if (action === 'claim') celebrate('small');
      await load();
    }
    setBusy(b => ({ ...b, [postId]: false }));
  }

  if (phase === 'loading') return <Wrap><div style={{ textAlign: 'center', color: B.MUT, padding: '60px 0' }}>Loading calendar…</div></Wrap>;
  if (phase === 'missing') return <Wrap><div style={{ textAlign: 'center', color: B.CAP, padding: '60px 0' }}>This calendar link is no longer available.</div></Wrap>;

  const d = cal.data || {};
  const assignedIds = d.assignment?.assignedToIds || [];
  const roster = (assignedIds.length ? assignedIds : emps.map(e => e.id)).filter(Boolean);
  const slug = logoForClientName(cal.clientName || '');

  const Header = (
    <div style={{ textAlign: 'center', marginBottom: 30 }}>
      {slug
        ? <img src={logoUrl(slug, 'white')} alt="" style={{ maxHeight: 66, maxWidth: 260, objectFit: 'contain', background: B.SURF, padding: '6px 12px', borderRadius: 'var(--radius)' }} />
        : <div style={{ fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 28 }}>{cal.clientName}</div>}
      <div style={{ marginTop: 8, fontSize: 12, fontWeight: 700, letterSpacing: 4, textTransform: 'uppercase', color: B.GOLD, fontFamily: 'var(--mono)' }}>{monthLabel(cal.monthYear)} Content Calendar</div>
    </div>
  );

  // ── Name gate — pick who you are (once per device) ──────────────
  if (!me || !roster.includes(me)) {
    return <Wrap>
      {Header}
      <div style={{ maxWidth: 440, margin: '10px auto 0', background: B.SURF, border: `1px solid ${B.LINE}`, borderRadius: 'var(--radius)', padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Who are you?</div>
        <div style={{ fontSize: 13, color: B.MUT, marginBottom: 18 }}>Tap your name so your work is credited. It's remembered on this device.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {emps.length === 0
            ? <div style={{ fontSize: 13, color: B.FAINT }}>Loading…</div>
            : roster.map(id => (
              <button key={id} onClick={() => pickMe(id)} style={{ padding: '11px 14px', fontSize: 14, fontWeight: 600, background: B.SURF2, border: `1px solid ${B.LINE}`, borderRadius: 'var(--radius)', color: B.TXT, cursor: 'pointer', fontFamily: 'inherit' }}>{empName(emps, id)}</button>
            ))}
          {emps.length > 0 && roster.length === 0 && <div style={{ fontSize: 13, color: B.FAINT }}>No designers are assigned to this calendar yet.</div>}
        </div>
      </div>
    </Wrap>;
  }

  // ── Flatten posts with grid + running numbers ───────────────────
  const grids = d.grids || [];
  let n = 0;
  const rows = grids.map((g, gi) => ({ gi, link: g.link, posts: (g.posts || []).map(p => ({ ...p, no: ++n, gi })) }));

  return <Wrap>
    {Header}

    {peers.length > 1 && <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}><PresenceBar users={peers} meId={me} dark /></div>}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 24, fontSize: 12.5, color: B.MUT, flexWrap: 'wrap' }}>
      <span>You are <span style={{ color: B.GOLD, fontWeight: 600 }}>{empName(emps, me)}</span></span>
      <span style={{ color: B.FAINT }}>·</span>
      <button onClick={() => { setMe(''); try { localStorage.removeItem('cal_me_id'); localStorage.removeItem('str_emp_id'); } catch (e) { /* */ } }} style={{ background: 'none', border: 'none', color: B.MUT, textDecoration: 'underline', cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit' }}>not you?</button>
      <span style={{ color: B.FAINT }}>·</span>
      <span style={{ color: B.FAINT }}>edits save for everyone live</span>
    </div>

    {rows.map(row => (
      <div key={row.gi} style={{ marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: B.FAINT, fontFamily: 'var(--mono)' }}>Row {row.gi + 1}</span>
          {row.link && <a href={row.link} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: B.GOLD }}>Row grid link ↗</a>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16 }}>
          {row.posts.map(post => {
            const st = STATUS[post.designStatus] || STATUS.todo;
            const mine = (post.designedBy || []).includes(me);
            const locked = post.designStatus === 'approved';
            const awaiting = !!post.awaitingClient;
            const linkVal = linkEdits[post.id] !== undefined ? linkEdits[post.id] : (post.fileLink || '');
            return (
              <div key={post.id} style={{ background: B.SURF, border: `1px solid ${B.LINE}`, borderLeft: post.designStatus === 'changes' ? `2px solid #C79A5B` : post.designStatus === 'approved' ? '2px solid #8FB08A' : `1px solid ${B.LINE}`, borderRadius: 'var(--radius)', padding: 14, display: 'flex', flexDirection: 'column', gap: 9 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: B.TXT }}>#{post.no}</span>
                  <span style={{ ...pill(STATUS.approved), background: 'rgba(143,176,138,0.14)', color: '#8FB08A' }}>{post.type || 'Post'}</span>
                  <span style={{ marginLeft: 'auto', ...pill(st) }}>{st.label}</span>
                </div>
                {post.objective && <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: B.FAINT }}>{post.objective}</div>}
                {post.postContent && <div style={{ fontSize: 13, lineHeight: 1.5, color: B.TXT, whiteSpace: 'pre-wrap' }}>{post.postContent}</div>}
                {post.caption && <div style={{ fontSize: 12.5, lineHeight: 1.5, color: B.CAP, whiteSpace: 'pre-wrap', borderLeft: `2px solid ${B.GOLD}`, paddingLeft: 9 }}>{post.caption}</div>}
                {post.reelCoverNote && <div style={{ fontSize: 11.5, color: B.MUT }}>Cover: {post.reelCoverNote}</div>}
                {post.inspiration && <div style={{ fontSize: 12, lineHeight: 1.5, color: B.CAP }}><span style={{ color: B.FAINT, fontWeight: 700, textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.6 }}>Inspiration </span><Linky text={post.inspiration} /></div>}
                {post.contentLinks && <div style={{ fontSize: 12, lineHeight: 1.5, color: B.CAP }}><span style={{ color: B.FAINT, fontWeight: 700, textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.6 }}>Raw content </span><Linky text={post.contentLinks} /></div>}

                {post.designStatus === 'changes' && post.reviewNote && (
                  <div style={{ fontSize: 12, color: '#C79A5B', background: 'rgba(199,154,91,0.12)', borderRadius: 'var(--radius)', padding: '7px 9px' }}>Changes: {post.reviewNote}</div>
                )}
                {awaiting && (
                  <div style={{ fontSize: 12, color: '#C79A5B', background: 'rgba(199,154,91,0.12)', borderRadius: 'var(--radius)', padding: '7px 9px', fontWeight: 600 }}>⏳ Waiting on client content{post.awaitingNote ? ` — ${post.awaitingNote}` : ''}. Skip this one for now.</div>
                )}

                <div style={{ marginTop: 2 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: B.FAINT, marginBottom: 4, fontFamily: 'var(--mono)' }}>Asset link</div>
                  <input style={inp} value={linkVal} placeholder="Paste your Drive / Canva link…" disabled={locked || awaiting || busy[post.id]}
                    onChange={e => setLinkEdits(m => ({ ...m, [post.id]: e.target.value }))}
                    onBlur={() => { const v = linkEdits[post.id]; if (v !== undefined && v !== (post.fileLink || '')) act(post.id, 'link', v); setLinkEdits(m => { const x = { ...m }; delete x[post.id]; return x; }); }} />
                </div>

                {(post.designedBy || []).length > 0 && <div style={{ fontSize: 11, color: B.FAINT }}>by {post.designedBy.map(id => empName(emps, id)).join(', ')}</div>}

                {!locked && !awaiting && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 2 }}>
                    <button onClick={() => act(post.id, mine ? 'unclaim' : 'claim')} disabled={busy[post.id]} style={{ fontSize: 12.5, fontWeight: 600, padding: '7px 12px', borderRadius: 'var(--radius)', cursor: 'pointer', fontFamily: 'inherit', border: `1px solid ${mine ? B.GOLD : B.LINE}`, background: mine ? 'rgba(169,137,83,0.14)' : 'transparent', color: mine ? B.GOLD : B.CAP }}>
                      {mine ? '✓ I’m on this' : 'I’m working on this'}
                    </button>
                    {post.designStatus !== 'submitted' && (
                      <button onClick={() => act(post.id, 'done', linkEdits[post.id])} disabled={busy[post.id]} style={{ fontSize: 12.5, fontWeight: 600, padding: '7px 14px', borderRadius: 'var(--radius)', cursor: 'pointer', fontFamily: 'inherit', border: 'none', background: B.GOLD, color: B.INK }}>
                        {busy[post.id] ? 'Saving…' : 'Mark done'}
                      </button>
                    )}
                    {post.designStatus === 'submitted' && <span style={{ fontSize: 12, color: '#A98953' }}>Submitted — waiting for review</span>}
                  </div>
                )}
                {locked && <div style={{ fontSize: 12, color: '#8FB08A', fontWeight: 600 }}>Approved ✓</div>}
              </div>
            );
          })}
        </div>
      </div>
    ))}

    <div style={{ marginTop: 44, borderTop: `1px solid ${B.LINE}`, paddingTop: 26, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 24, letterSpacing: '0.28em', textIndent: '0.28em', color: B.TXT, lineHeight: 1 }}>STR</div>
      <div style={{ width: 30, height: 1, background: B.GOLD, opacity: 0.6, margin: '11px 0 8px' }} />
      <div style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.28em', textTransform: 'uppercase', color: B.FAINT }}>Strategists</div>
    </div>

    {toast && <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: B.SURF2, border: `1px solid ${B.LINE}`, color: B.TXT, fontSize: 13, padding: '10px 18px', borderRadius: 'var(--radius)', zIndex: 50 }}>{toast}</div>}
  </Wrap>;
}
